import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./App.css";
import emailjs from "emailjs-com";
import Logo from "./assets/logo_simple_white.svg";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.6, ease: "easeOut" },
};

/* Animated particle network for the hero background */
function ParticleField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let raf = null;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      const count = Math.min(90, Math.floor(canvas.width / (18 * dpr)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2 * dpr,
        vy: (Math.random() - 0.5) * 0.2 * dpr,
        r: (Math.random() * 1.4 + 0.6) * dpr,
      }));
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const linkDist = 130 * dpr;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 194, 209, 0.45)";
        ctx.fill();
      }
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.hypot(dx, dy);
          if (d < linkDist) {
            ctx.strokeStyle = `rgba(0, 194, 209, ${0.15 * (1 - d / linkDist)})`;
            ctx.lineWidth = 0.7 * dpr;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const loop = () => {
      drawFrame();
      raf = requestAnimationFrame(loop);
    };

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    resize();
    if (reducedMotion) {
      drawFrame();
    } else {
      loop();
    }

    const onResize = () => {
      resize();
      if (reducedMotion) drawFrame();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-canvas" aria-hidden="true" />;
}

/* Pyramid diagram of how Datify's practices build toward its mission */
function ServicePyramid() {
  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.16 } },
  };
  const tierVariant = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
  };

  return (
    <motion.svg
      className="service-pyramid"
      viewBox="0 0 640 410"
      role="img"
      aria-label="Pyramid diagram: investment at the base, data consulting, company value, and tech strategy above it, synergy among portfolio companies next, and smiles at the top"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.35 }}
      variants={stagger}
    >
      <defs>
        <linearGradient id="spAmber" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f9c95c" />
          <stop offset="100%" stopColor="#e0961d" />
        </linearGradient>
        <linearGradient id="spLight" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f4f8f9" />
          <stop offset="100%" stopColor="#ccdadd" />
        </linearGradient>
        <linearGradient id="spBase" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#149aac" />
          <stop offset="100%" stopColor="#0a5866" />
        </linearGradient>
        <linearGradient id="spCell" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00c2d1" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#00c2d1" stopOpacity="0.07" />
        </linearGradient>
        <radialGradient id="spGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#00c2d1" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#00c2d1" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="320" cy="386" rx="300" ry="20" fill="url(#spGlow)" />

      <motion.g variants={tierVariant}>
        <polygon
          points="96,280 544,280 614.4,368 25.6,368"
          fill="url(#spBase)"
          stroke="url(#spBase)"
          strokeWidth="8"
          strokeLinejoin="round"
        />
        <text className="sp-text-light" x="320" y="332">Investment</text>
      </motion.g>

      <motion.g variants={tierVariant}>
        <polygon
          points="172.8,184 252,184 252,272 102.4,272"
          fill="url(#spCell)"
          stroke="#00c2d1"
          strokeOpacity="0.45"
          strokeWidth="1"
        />
        <text className="sp-text-teal" x="196" y="222">Data</text>
        <text className="sp-text-teal" x="192" y="244">Consulting</text>

        <polygon
          points="260,184 380,184 380,272 260,272"
          fill="url(#spLight)"
          stroke="url(#spLight)"
          strokeWidth="6"
          strokeLinejoin="round"
        />
        <text className="sp-text-dark" x="320" y="216">Company</text>
        <text className="sp-text-dark" x="320" y="236">Value</text>
        <text className="sp-text-dark sp-small" x="320" y="256">(Core)</text>

        <polygon
          points="388,184 467.2,184 537.6,272 388,272"
          fill="url(#spCell)"
          stroke="#00c2d1"
          strokeOpacity="0.45"
          strokeWidth="1"
        />
        <text className="sp-text-teal" x="444" y="222">Tech</text>
        <text className="sp-text-teal" x="448" y="244">Strategy</text>
      </motion.g>

      <motion.g variants={tierVariant}>
        <polygon
          points="236.8,104 403.2,104 460.8,176 179.2,176"
          fill="url(#spLight)"
          stroke="url(#spLight)"
          strokeWidth="6"
          strokeLinejoin="round"
        />
        <text className="sp-text-dark" x="320" y="136">Synergy among</text>
        <text className="sp-text-dark" x="320" y="158">Portfolio Companies</text>
      </motion.g>

      <motion.g variants={tierVariant}>
        <polygon
          points="320,4 396.8,96 243.2,96"
          fill="url(#spAmber)"
          stroke="url(#spAmber)"
          strokeWidth="8"
          strokeLinejoin="round"
        />
        <text className="sp-text-dark" x="320" y="82">Smiles</text>
      </motion.g>
    </motion.svg>
  );
}

const serviceIcons = {
  consulting: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </svg>
  ),
  investment: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
  strategy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  ),
};

const services = [
  {
    icon: "consulting",
    title: "Data Consulting",
    description:
      "We help businesses leverage data for better decision-making through analytics and AI solutions.",
  },
  {
    icon: "investment",
    title: "Investment",
    description:
      "We invest in high-potential startups, providing strategic guidance and financial support.",
  },
  {
    icon: "strategy",
    title: "Tech Strategy",
    description:
      "We provide technology roadmaps and digital transformation strategies for business growth.",
  },
];

const pillars = [
  {
    title: "Technology-First Growth",
    description:
      "We prioritize the long-term impact of AI and data-driven solutions over short-term gains, supporting early-stage startups with investment, data consulting, and tech strategy.",
  },
  {
    title: "Strategic Synergy",
    description:
      "The core value, strength, and mission of each company comes first. We cultivate relationships with visionary leaders while each company keeps its unique value, privacy, and competitive edge.",
  },
  {
    title: "Industry Growth",
    description:
      "We foster an ecosystem where startups collaborate rather than compete in isolation, accelerating industry-wide progress and spreading smiles through impactful technology.",
  },
];

const team = [
  {
    name: "Shusei Yokoi",
    role: "CEO & Founder",
    description: (
      <>
        Passionate leader with a vision to revolutionize data-driven decision making.{" "}
        <a
          href="https://shuseiyokoi.github.io/portfolio"
          target="_blank"
          rel="noopener noreferrer"
          className="custom-link"
        >
          More about Shusei
        </a>
      </>
    ),
    image: "Shusei_Yokoi.jpg",
  },
  {
    name: "Yu Yumoto",
    role: "Director",
    description:
      "Backed by extensive experience in startup operations across both the U.S. and Japan.",
    image: "Yu_Yumoto.jpg",
  },
];

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = () => {
    setMenuOpen(false);
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);
    setStatus("");

    emailjs
      .send(
        "service_data_lp",
        "template_tkmw5qi",
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
        },
        "lQprQRoDEdHPOcWrK"
      )
      .then(
        () => {
          setStatus("Message sent successfully!");
          setFormData({ firstName: "", lastName: "", email: "", message: "" });
          setSending(false);
        },
        () => {
          setStatus("Failed to send message. Please try again.");
          setSending(false);
        }
      );
  };

  return (
    <div className="app-container">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <a href="#home" className="logo">
          <img src={Logo} alt="Datify" />
          <span className="logo-name">Datify</span>
        </a>

        <button
          className="mobile-menu-button"
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ☰
        </button>

        <div className={`nav-links ${menuOpen ? "open" : ""}`}>
          <ul>
            <li><a href="#service" onClick={handleNavClick}>Services</a></li>
            <li><a href="#about" onClick={handleNavClick}>About</a></li>
            <li><a href="#staff" onClick={handleNavClick}>Team</a></li>
          </ul>
          <a href="#contact" className="nav-cta" onClick={handleNavClick}>
            Contact us
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="hero">
        <ParticleField />
        <div className="hero-content">
          <motion.p
            className="hero-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Data consulting · Venture investment · Los Angeles
          </motion.p>
          <motion.h1
            className="hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Turning Data into Smiles,<br />
            Spreading Happiness Across the World.
          </motion.h1>
          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            We invest in high-potential startups and help businesses make better
            decisions with data science and AI strategy.
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <a href="#contact" className="btn btn-primary">Work with us</a>
            <a href="#service" className="btn btn-ghost">See our services</a>
          </motion.div>
        </div>
        <div className="hero-fade" aria-hidden="true" />
      </section>

      {/* Fact strip */}
      <div className="fact-strip">
        <div className="fact">
          <span className="fact-title">Los Angeles</span>
          <span className="fact-label">Where we are based</span>
        </div>
        <div className="fact">
          <span className="fact-title">U.S. &amp; Japan</span>
          <span className="fact-label">Where we operate</span>
        </div>
        <div className="fact">
          <span className="fact-title">Consulting · Investment · Strategy</span>
          <span className="fact-label">What we do</span>
        </div>
      </div>

      {/* Services */}
      <section id="service" className="section service-section">
        <motion.div className="section-heading" {...reveal}>
          <p className="section-eyebrow">Services</p>
          <h2>What we do</h2>
        </motion.div>
        <div className="service-container">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="service-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <div className="service-icon">{serviceIcons[service.icon]}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="section about-section">
        <motion.div className="section-heading" {...reveal}>
          <p className="section-eyebrow">About us</p>
          <h2>Investing in the future of technology itself</h2>
        </motion.div>
        <motion.p className="about-lead" {...reveal}>
          At Datify, our mission goes beyond financial returns. We invest in
          high-potential tech startups in Los Angeles — providing capital, data
          science, and technology strategy — and foster meaningful collaboration
          between companies while each retains its unique value, privacy, and
          competitive edge.
        </motion.p>
        <div className="pillar-container">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              className="pillar-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <span className="pillar-number">{String(index + 1).padStart(2, "0")}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.description}</p>
            </motion.div>
          ))}
        </div>
        <motion.div className="about-image" {...reveal}>
          <ServicePyramid />
        </motion.div>
      </section>

      {/* Team */}
      <section id="staff" className="section team-section">
        <motion.div className="section-heading" {...reveal}>
          <p className="section-eyebrow">Team</p>
          <h2>The people behind Datify</h2>
        </motion.div>
        <div className="team-container">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              className="team-member"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/${member.image}`}
                alt={member.name}
                className="team-image"
              />
              <div className="team-info">
                <h3>{member.name}</h3>
                <p className="team-role">{member.role}</p>
                <p>{member.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section contact-section">
        <motion.div className="section-heading" {...reveal}>
          <p className="section-eyebrow">Contact</p>
          <h2>Let's build something together</h2>
          <p className="contact-sub">
            Tell us about your company or your data challenge — we usually reply
            within a few days.
          </p>
        </motion.div>
        <motion.form onSubmit={sendEmail} className="contact-form" {...reveal}>
          <div className="input-group">
            <input type="text" name="firstName" placeholder="First name" value={formData.firstName} onChange={handleChange} required />
            <input type="text" name="lastName" placeholder="Last name" value={formData.lastName} onChange={handleChange} required />
          </div>
          <input type="email" name="email" placeholder="Your email" value={formData.email} onChange={handleChange} required />
          <textarea name="message" placeholder="Your message" value={formData.message} onChange={handleChange} required />
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? "Sending..." : "Send message"}
          </button>
        </motion.form>
        {status && <p className="status-message">{status}</p>}
      </section>

      <footer>
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={Logo} alt="Datify" />
            <span>Datify</span>
          </div>
          <ul className="footer-links">
            <li><a href="#service">Services</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#staff">Team</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
        <div className="footer-legal">
          <p>© 2026 Datify LLC. All rights reserved.</p>
          <p>Datify LLC is a California limited liability company.</p>
        </div>
      </footer>
    </div>
  );
}
